import React from 'react'
import type { FC } from 'react'
import { useSelector } from 'react-redux'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import { Grid } from '@material-ui/core'

import { getUser } from '@homeviews/store'
import { HAS_SOME_DEVELOPMENT_PERMISSIONS } from '@homeviews/users'
import { Card } from '@homeviews/shared-ui'
import type { Recommendation } from '@homeviews/analytics'
import {
  Typography,
  GoToButton,
  dateToString,
  Statistic,
  Recommendations,
  Table,
} from '@homeviews/analytics'
import type {
  Reviews,
  MetaTotal,
  AreaGuideBenchmark,
  AreaGuides,
} from '@homeviews/analytics'

const useStyles = makeStyles((theme) =>
  createStyles({
    wrapper: {
      marginBottom: '40px',
      paddingBottom: '30px',
    },
    reviewsWrapper: {
      marginBottom: '16px',
    },
    reviewCard: {
      height: '192px',
      borderColor: 'transparent',
      backgroundColor: theme.colors.grey200,
    },
    reviewBody: {
      height: '90%',
    },
    customBody1Title: {
      '.MuiTypography-body1&': {
        display: '-webkit-box',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': 3,
        overflow: 'hidden',
        fontSize: '18px',
        lineHeight: '24px',
        marginBottom: '12px',
      },
    },
    customBody1DName: {
      '.MuiTypography-body1&': {
        display: '-webkit-box',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': 2,
        overflow: 'hidden',
      },
    },
    reviewFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& .MuiButtonBase-root': {
        marginLeft: '0',
      },
    },
    guidesWrapper: {
      marginBottom: '16px',
    },
  }),
)

type GuideDevelopment = {
  name: string
  reviewCount: number
  transparencyScore: string // 'Red' | 'Amber' | 'Green'
}

type MarketingCollateralProps = {
  reviews: Reviews
  dmentAreasInGuidesLoading: boolean
  dmentAreasInGuides: MetaTotal
  dmentsTotalInTopGuidesLoading: boolean
  dmentsTotalInTopGuides: MetaTotal
  dmentsTotalLoading: boolean
  dmentsTotal: MetaTotal
  guideBenchmarkLoading: boolean
  guideBenchmark: AreaGuideBenchmark
  dmentsTotalInRedScoreLoading: boolean
  dmentsTotalInRedScore: MetaTotal
  areaGuides: AreaGuides
}

export const MarketingCollateral: FC<MarketingCollateralProps> = ({
  reviews,
  dmentAreasInGuidesLoading,
  dmentAreasInGuides,
  dmentsTotalInTopGuidesLoading,
  dmentsTotalInTopGuides,
  dmentsTotalLoading,
  dmentsTotal,
  guideBenchmarkLoading,
  guideBenchmark,
  dmentsTotalInRedScoreLoading,
  dmentsTotalInRedScore,
  areaGuides,
}) => {
  const classes = useStyles()
  const user = useSelector(getUser)

  const guidesFeaturedDataIsSet = () =>
    dmentAreasInGuides?.meta?.total !== undefined
  const guidesFeaturedValue = () => {
    if (!guidesFeaturedDataIsSet()) return undefined
    return dmentAreasInGuides.meta.total.toLocaleString()
  }

  const topGuidesDataIsSet = () =>
    dmentsTotalInTopGuides?.meta?.total !== undefined &&
    dmentsTotal?.meta?.total !== undefined &&
    guideBenchmark?.data?.top?.['10'] !== undefined
  const topGuidesValue = () => {
    if (!topGuidesDataIsSet()) return undefined
    return dmentsTotalInTopGuides.meta.total.toLocaleString()
  }
  const topGuidesIsAboveBenchmark = () =>
    topGuidesDataIsSet() &&
    dmentsTotalInTopGuides.meta.total / dmentsTotal.meta.total >=
      guideBenchmark?.data?.top?.['10'] / 100
  const topGuidesDescription = () => {
    if (!topGuidesDataIsSet()) return undefined
    if (topGuidesIsAboveBenchmark()) {
      return 'This puts you above the HomeViews average for top 10 developments on HomeViews.'
    } else {
      return 'This puts you below the HomeViews average for top 10 developments on HomeViews.'
    }
  }

  const redScoreDataIsSet = () =>
    dmentsTotalInRedScore?.meta?.total !== undefined
  const redScoreValue = () => {
    if (!redScoreDataIsSet()) return undefined
    return dmentsTotalInRedScore.meta.total.toLocaleString()
  }
  const redScoreIsAboveBenchmark = () =>
    redScoreDataIsSet() && dmentsTotalInRedScore?.meta?.total >= 2
  const redScoreDescription = () => {
    if (!redScoreDataIsSet()) return undefined
    if (redScoreIsAboveBenchmark()) {
      return 'This puts you above the recommended number of red transparency ratings on HomeViews.'
    } else {
      return 'This puts you below the recommended number of red transparency ratings on HomeViews.'
    }
  }
  const redScoreIsGoToDisabled = () =>
    !HAS_SOME_DEVELOPMENT_PERMISSIONS(user, [
      'UNDERTAKE_RESIDENT_OUTREACH_REQUESTS',
    ])

  const tableRowsDataIsSet = () => areaGuides?.data !== undefined
  const tableRowsData = () => {
    if (!tableRowsDataIsSet()) return []

    // e.g. ['East London', '2', 'Fizzy Poplar, E14', '97', 'Amber']
    const rows: Array<string[]> = []
    // e.g. { 'East London': { 2: {
    //   name: 'Fizzy Poplar, E14', reviewCount: 97, transparencyScore: 'Amber',
    // } } }
    const areas: Record<string, Record<number, GuideDevelopment>> = {}
    // Put API data in to an easily sortable data structure
    areaGuides.data.forEach((areaGuide) => {
      const aName = areaGuide.name
      areaGuide.developments.forEach((dment) => {
        const dmentRank = dment.rank
        if (!areas[aName]) areas[aName] = {}
        areas[aName][dmentRank] = {
          name: dment.name,
          reviewCount: dment.reviewCount,
          transparencyScore: dment.transparencyScore,
        }
      })
    })
    Object.keys(areas)
      .sort() // Firstly, sort rows by AreaName alphabetically A-Z first
      .forEach((areaName) => {
        Object.keys(areas[areaName])
          .sort() // Secondly, sort rows by Development Rank lowest-number-first
          .forEach((dmentRank) => {
            const dment = areas[areaName][parseInt(dmentRank)]
            rows.push([
              areaName,
              dmentRank,
              dment.name,
              dment.reviewCount.toLocaleString(),
              dment.transparencyScore,
            ])
          })
      })
    return rows
  }

  const recommendationsData = (): Array<Recommendation> => {
    const recommendationsArr = [] as Array<Recommendation>
    if (!topGuidesIsAboveBenchmark()) {
      recommendationsArr.push({
        description:
          'Rankings are dependent on number of verified reviews, star rating and transparency score for each development. Use the dashboard in the portal to see how you’re scoring on these elements.',
        goToHref: '/dashboard',
        goToDisabled: false,
      })
    }
    if (!redScoreIsAboveBenchmark()) {
      recommendationsArr.push({
        description:
          'Transparency scores are based off of the number of verified reviews you’ve had in the past 12 months. This score can impact where you are ranked on local area guides which receive a lot of web traffic. Use our outreach tool to collect more reviews.',
        goToHref: '/outreach',
        goToDisabled: redScoreIsGoToDisabled(),
      })
    }
    return recommendationsArr
  }

  return (
    <Card className={classes.wrapper}>
      <Typography variant="h2">Marketing Collateral</Typography>
      {reviews && reviews.data.length > 0 && (
        <>
          <Typography variant="h2">Most recent 5 star reviews</Typography>
          <Grid container spacing={3} className={classes.reviewsWrapper}>
            {reviews.data.slice(0, 5).map((review, _i, arr) => {
              const reviewDate = new Date(Date.parse(review.review_date))
              // When lg (width >= 1200px), let Grid elements share available space
              // equally to achieve 1/5 width each, otherwise show at 1/4 width each
              const lgAuto = arr.length === 5
              return (
                <Grid key={review.id} item xs={6} md={4} lg={lgAuto || 3}>
                  <Card className={classes.reviewCard}>
                    <div className={classes.reviewBody}>
                      <Typography
                        variant="body1"
                        className={classes.customBody1Title}
                      >
                        &ldquo;{review.title}&rdquo;
                      </Typography>
                      <Typography
                        variant="body1"
                        className={classes.customBody1DName}
                      >
                        {review.development_name}
                      </Typography>
                    </div>
                    <div className={classes.reviewFooter}>
                      <Typography variant="body2">
                        {dateToString(reviewDate)}
                      </Typography>
                      <GoToButton
                        href={`/reviews?review=${review.id}`}
                        trackingEventName="Analytics: 5 star review: Go to"
                      />
                    </div>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </>
      )}

      <Typography variant="h2">Area Guides Featured in HomeViews</Typography>
      <Grid container spacing={3} className={classes.guidesWrapper}>
        <Grid item xs={4}>
          <Statistic
            isLoading={dmentAreasInGuidesLoading}
            title="Area Guides Featured In"
            goToHref=""
            goToIsDisabled={false}
            value={guidesFeaturedValue()}
            description=""
          />
        </Grid>
        <Grid item xs={4}>
          <Statistic
            isLoading={
              dmentsTotalLoading ||
              dmentsTotalInTopGuidesLoading ||
              guideBenchmarkLoading
            }
            title="Developments in top 10 Area Guide"
            goToHref=""
            goToIsDisabled={false}
            value={topGuidesValue()}
            description={topGuidesDescription()}
          />
        </Grid>
        <Grid item xs={4}>
          <Statistic
            isLoading={dmentsTotalInRedScoreLoading}
            title="Transparency Score Red"
            goToHref=""
            goToIsDisabled={false}
            value={redScoreValue()}
            description={redScoreDescription()}
          />
        </Grid>
      </Grid>
      <Table
        columns={[
          'Area Guide',
          'Rank',
          'Development',
          'Number of Reviews',
          'Transparency Score',
        ]}
        rows={tableRowsData()}
      />
      <Recommendations
        data={
          dmentsTotal &&
          dmentsTotalInTopGuides &&
          guideBenchmark &&
          recommendationsData()
        }
        goToTrackingEventName="Analytics: Marketing Recommendation: Go to"
      />
    </Card>
  )
}
